<div class="audit-logs-container">
  <div class="mb-3 d-flex justify-content-between align-items-center">
    <div class="text-muted" #jumpToElement>
      <span>Showing: {{displayLogs.length}} @if (totalCount > 0) { of {{totalCount | number}} }</span>
    </div>
  </div>

  @if (displayLogs.length === 0 && !isLoading) {
    <div class="d-flex flex-column align-items-center justify-content-center text-center py-5">
      <fa-duotone-icon 
        icon="clock-rotate-left" 
        class="fs-1 text-secondary opacity-50 mb-3"
        secondaryOpacity="0.3"
      ></fa-duotone-icon>
      <p class="fs-5 text-secondary fw-medium mb-0">No audit logs found</p>
    </div>
  } @else {
    <!-- Desktop View -->
    <div class="d-none d-md-flex flex-column gap-3">
      @for (log of displayLogs; track log.timestamp; let i = $index) {
        <div 
          class="audit-log-item border rounded-3 overflow-hidden placeholder-glow"
          [class.cursor-pointer]="isRowExpandable(log) && !isLoading"
          [class.expanded]="expandedRows().has(i)"
          (click)="!isLoading && isRowExpandable(log) && toggleRow(i)"
        >
          <div class="d-flex p-3 gap-3 align-items-center">
            <div class="flex-grow-1">
              <h3 class="fs-6 fw-medium mb-0 log-title text-truncate" [class.placeholder]="isLoading"
                [msTooltip]="log.messageTitle" [msTooltipDisabled]="isLoading" msTooltipOnlyIfTruncated>{{ log.messageTitle }}</h3>
              @if (canViewDetails() && log.actingEntityName) {
                <small class="text-muted" [class.placeholder]="isLoading">
                  by
                  @if (canViewUsers() && log.actingEntityId) {
                    <a [routerLink]="['/users/profile', log.actingEntityId]"
                       (click)="$event.stopPropagation()"
                       class="acting-user-link">{{ log.actingEntityName }}</a>
                  } @else {
                    {{ log.actingEntityName }}
                  }
                </small>
              }
            </div>
            <span class="log-date small text-nowrap" [class.placeholder]="isLoading">{{ log.timestamp | orgDate:'medium' }}</span>
            <div class="flex-shrink-0 d-flex bg-body-secondary rounded-2 chevron-container" [class.placeholder]="isLoading">
              <fa-duotone-icon
                icon="chevron-down"
                [hidden]="!isRowExpandable(log) || isLoading"
                class="chevron-icon"
                secondaryOpacity="0.9"
              ></fa-duotone-icon>
            </div>
          </div>

          @if (!isLoading) {
            <div class="audit-log-details" [class.show]="expandedRows().has(i)">
              <div class="details-content px-3 pb-3">
                @if (log.messageDetails && log.messageDetails.length > 0) {
                  <div class="mb-3">
                    <h4 class="small text-primary fw-semibold text-uppercase mb-2">Details</h4>
                    <ul class="audit-log-ul mb-0 d-flex flex-column gap-2">
                      @for (messageDetail of log.messageDetails; track messageDetail) {
                        <li>
                          <span class="text-body" [innerHTML]="messageDetail.detail | SafeHtml"></span>
                          @if (messageDetail.subDetails && messageDetail.subDetails.length > 0) {
                            <ul class="mt-2 d-flex flex-column gap-1">
                              @for (subDetail of messageDetail.subDetails; track subDetail) {
                                <li>
                                  <span class="subdetail text-body">
                                    {{ subDetail }}
                                  </span>
                                </li>
                              }
                            </ul>
                          }
                        </li>
                      }
                    </ul>
                  </div>
                }

                @if (canViewDetails() && log.note) {
                  <div class="pt-3 border-top">
                    <h4 class="small text-primary fw-semibold text-uppercase mb-2">Note</h4>
                    <p class="mb-0 p-3 border-start border-primary border-3 rounded-2">
                      {{ log.note }}
                    </p>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Mobile View -->
    <div class="d-flex d-md-none flex-column gap-3">
      
      @for (log of displayLogs; track log.timestamp; let i = $index) {
        <div 
          class="audit-log-item border rounded-3 overflow-hidden placeholder-glow"
          [class.cursor-pointer]="isRowExpandable(log) && !isLoading"
          [class.expanded]="expandedRows().has(i)"
          (click)="!isLoading && isRowExpandable(log) && toggleRow(i)"
        >
          <div class="d-flex p-3 gap-3 align-items-center">
            <div class="flex-grow-1">
              <h3 class="fs-6 fw-medium mb-1 log-title text-truncate" [class.placeholder]="isLoading"
                [msTooltip]="log.messageTitle" [msTooltipDisabled]="isLoading" msTooltipOnlyIfTruncated>{{ log.messageTitle }}</h3>
              @if (canViewDetails() && log.actingEntityName) {
                <small class="text-muted d-block mb-1" [class.placeholder]="isLoading">
                  by
                  @if (canViewUsers() && log.actingEntityId) {
                    <a [routerLink]="['/users/profile', log.actingEntityId]"
                       (click)="$event.stopPropagation()"
                       class="acting-user-link">{{ log.actingEntityName }}</a>
                  } @else {
                    {{ log.actingEntityName }}
                  }
                </small>
              }
              <span class="log-date small text-nowrap" [class.placeholder]="isLoading">{{ log.timestamp | orgDate:'medium' }}</span>
            </div>
            <div class="flex-shrink-0 d-flex align-items-center justify-content-center bg-body-secondary rounded-2 chevron-container" [class.placeholder]="isLoading">
              <fa-duotone-icon 
                icon="chevron-down" 
                [hidden]="!isRowExpandable(log) || isLoading"
                class="chevron-icon"
                secondaryOpacity="0.9"
              ></fa-duotone-icon>
            </div>
          </div>
          
          @if (!isLoading) {
            <div class="audit-log-details" [class.show]="expandedRows().has(i)">
              <div class="details-content px-3 pb-3">
                @if (log.messageDetails && log.messageDetails.length > 0) {
                  <div class="mb-3">
                    <h4 class="small text-primary fw-semibold text-uppercase mb-2">Details</h4>
                    <ul class="audit-log-ul mb-0 d-flex flex-column gap-2">
                      @for (detail of log.messageDetails; track detail) {
                        <li>
                          <span class="text-body" [innerHTML]="detail.detail | SafeHtml"></span>
                          @if (detail.subDetails && detail.subDetails.length > 0) {
                            <ul class="mt-2 d-flex flex-column gap-1">
                              @for (subDetail of detail.subDetails; track subDetail) {
                                <li>
                                  <span class="subdetail text-body">
                                    {{ subDetail }}
                                  </span>
                                </li>
                              }
                            </ul>
                          }
                        </li>
                      }
                    </ul>
                  </div>
                }
                
                @if (canViewDetails() && log.note) {
                  <div class="pt-3 border-top">
                    <h4 class="small text-primary fw-semibold text-uppercase mb-2">Note</h4>
                    <p class="mb-0 p-3 border-start border-primary border-3 rounded-2">
                      {{ log.note }}
                    </p>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Pagination -->
    <div class="mt-2 pagination-container" ms-pagination 
      [take]="take" 
      (loadMoreInView)="loadMoreInView()" 
      [(skip)]="skip"
      [hasNext]="hasNext" 
      [itemCount]="displayLogs.length" 
      (jumpToClicked)="jumpToTop($event)">
    </div>
  }
</div>
